/**
 * Core gamification (P0): Campaign (Boss / Wonder) capacity + pre-cap contribution credit and
 * the enrollment/deadline/teamTarget legality asserts locked in
 * `docs/plans/core-gamification.plan.md` (Campaign section). Pure functions only —
 * persistence / `FOR UPDATE` locking lives in the API layer
 * (`packages/api/modules/progress-actions/application/credit-campaign-progress.ts`).
 */

import { priorityMultiplier, type TaskPriority } from "./gamification-progress-action";

export type ProgressCampaignKind = "BOSS" | "WONDER";
export type TeamTargetMode = "COUNT" | "PERCENT";
export type TeamTargetBasis = "STATIC_SNAPSHOT" | "DYNAMIC_LIVE";
export type TeamTargetSnapshotSet = "ELIGIBLE_AT_ISSUE" | "ENROLLED_AT_ENROLLMENT_CLOSE";
export type CampaignEnrollmentMode = "OPT_IN" | "OPT_OUT";

/** Shape of the `CampaignTask.teamTarget` / `CampaignHabit.teamTarget` Json column. */
export interface TeamTargetJson {
	version?: number;
	mode: TeamTargetMode;
	basis: TeamTargetBasis;
	value: number;
	snapshotSet?: TeamTargetSnapshotSet;
	snapshotAudienceSize?: number;
	snapshotAt?: string;
}

export interface CampaignComponentCapacityInput {
	targetCount: number;
	estimatedMinutes: number;
	priority: TaskPriority;
}

/** Per-component weight: `targetCount × minutes × priorityMultiplier` (HP/build capacity unit). */
export function componentWeight(input: CampaignComponentCapacityInput): number {
	const targetCount = Number.isFinite(input.targetCount) ? Math.max(0, input.targetCount) : 0;
	const minutes = Number.isFinite(input.estimatedMinutes)
		? Math.max(0, input.estimatedMinutes)
		: 0;
	return targetCount * minutes * priorityMultiplier(input.priority);
}

/** `HP/build capacity = Σ(targetCount × minutes × priorityMultiplier)` across all components. */
export function campaignCapacity(
	components: readonly CampaignComponentCapacityInput[],
): number {
	return components.reduce((sum, component) => sum + componentWeight(component), 0);
}

/**
 * Resolves the effective COUNT target from a `teamTarget` Json (COUNT passthrough; PERCENT
 * resolves against the STATIC snapshot audience or a supplied live audience size for
 * DYNAMIC_LIVE — Boss only, D-locked).
 */
export function resolveTeamTargetCount(input: {
	teamTarget: TeamTargetJson;
	liveAudienceSize?: number;
}): number {
	const { teamTarget } = input;
	if (teamTarget.mode === "COUNT") {
		return Math.max(0, Math.round(teamTarget.value));
	}
	const audienceSize =
		teamTarget.basis === "STATIC_SNAPSHOT"
			? (teamTarget.snapshotAudienceSize ?? 0)
			: (input.liveAudienceSize ?? teamTarget.snapshotAudienceSize ?? 0);
	const clampedPercent = Math.min(100, Math.max(0, teamTarget.value));
	return Math.ceil((clampedPercent / 100) * Math.max(0, audienceSize));
}

export interface IsEligibleForCampaignCreditParams {
	creditedCompletions: number;
	targetCount: number;
}

/** Pre-cap gate: only credit while `creditedCompletions < targetCount` (AC 0.4). */
export function isEligibleForCampaignCredit(
	params: IsEligibleForCampaignCreditParams,
): boolean {
	return params.creditedCompletions < params.targetCount;
}

export interface ComputeContributionWeightParams {
	creditedCompletions: number;
	targetCount: number;
	unitWeight: number;
}

export interface ComputeContributionWeightResult {
	credit: number;
	nextCreditedCompletions: number;
}

/**
 * Pre-cap credit for a single completion/period-tick event against a campaign component.
 * Callers persist `creditedCompletions` under `FOR UPDATE`; this is the pure decision only.
 */
export function computeContributionWeight(
	params: ComputeContributionWeightParams,
): ComputeContributionWeightResult {
	if (!isEligibleForCampaignCredit(params)) {
		return { credit: 0, nextCreditedCompletions: params.creditedCompletions };
	}
	return {
		credit: Math.max(0, params.unitWeight),
		nextCreditedCompletions: params.creditedCompletions + 1,
	};
}

/** Alias of `computeContributionWeight` — both names kept for consumer compatibility. */
export const computePreCapContributionCredit = computeContributionWeight;

/**
 * Wonder team targets STATIC only; Boss may STATIC or DYNAMIC (<100% PERCENT only when a
 * deadline is set). Throws on violation — callers wrap in FORBIDDEN/CONFLICT as appropriate.
 */
export function assertTeamTargetValidForCampaignKind(
	kind: ProgressCampaignKind,
	target: TeamTargetJson,
	options?: { hasDeadline?: boolean },
): void {
	if (kind === "WONDER" && target.basis !== "STATIC_SNAPSHOT") {
		throw new Error("Wonder campaigns require STATIC_SNAPSHOT team targets.");
	}
	if (target.basis === "DYNAMIC_LIVE" && target.mode === "PERCENT") {
		if (!options?.hasDeadline) {
			throw new Error("DYNAMIC_LIVE PERCENT team targets require a deadline.");
		}
		if (target.value >= 100) {
			throw new Error(
				"DYNAMIC_LIVE PERCENT team targets must be < 100% when a deadline is set.",
			);
		}
	}
}

/**
 * `enrollmentClosesAt` is a hard cutoff; for Wonder it must be
 * `<= deadlineAt - maxHabitDuration` so every enrolled habit can complete before the deadline.
 */
export function assertEnrollmentWindowValid(params: {
	kind: ProgressCampaignKind;
	enrollmentClosesAt: Date | null;
	deadlineAt: Date | null;
	maxHabitDurationMs?: number;
}): void {
	const { kind, enrollmentClosesAt, deadlineAt, maxHabitDurationMs = 0 } = params;
	if (!enrollmentClosesAt) {
		throw new Error("enrollmentClosesAt is required (hard enrollment cutoff).");
	}
	if (kind === "WONDER" && deadlineAt) {
		const latestAllowedMs = deadlineAt.getTime() - maxHabitDurationMs;
		if (enrollmentClosesAt.getTime() > latestAllowedMs) {
			throw new Error(
				"Wonder enrollmentClosesAt must be <= deadlineAt - maxHabitDuration.",
			);
		}
	}
}

/** Deadline (when set) must be strictly after enrollment close — sanity guard shared by both kinds. */
export function assertDeadlineAfterEnrollmentClose(params: {
	enrollmentClosesAt: Date | null;
	deadlineAt: Date | null;
}): void {
	if (params.enrollmentClosesAt && params.deadlineAt) {
		if (params.deadlineAt.getTime() <= params.enrollmentClosesAt.getTime()) {
			throw new Error("deadlineAt must be after enrollmentClosesAt.");
		}
	}
}

/** OPT_IN snapshots at enrollment close; OPT_OUT snapshots at issue (D-locked bridge). */
export function resolveTeamTargetSnapshotSet(
	enrollmentMode: CampaignEnrollmentMode,
): TeamTargetSnapshotSet {
	return enrollmentMode === "OPT_OUT" ? "ELIGIBLE_AT_ISSUE" : "ENROLLED_AT_ENROLLMENT_CLOSE";
}

/**
 * On first publish, if `issuedAt` is null, set it to `publishedAt` (OPT_OUT eligibility bridge —
 * D10). Subsequent publishes must not move `issuedAt` (multi-issue CRM is deferred).
 */
export function resolveIssuedAtOnPublish(params: {
	issuedAt: Date | null;
	publishedAt: Date;
}): Date {
	return params.issuedAt ?? params.publishedAt;
}

export interface CampaignSnapshotAudience {
	snapshotSet: TeamTargetSnapshotSet;
	snapshotAudienceSize: number;
	snapshotAt: Date;
}

/** Freeze a STATIC_SNAPSHOT team target's audience-derived fields at close/issue time. */
export function buildStaticSnapshot(
	audienceSize: number,
	snapshotSet: TeamTargetSnapshotSet,
	snapshotAt: Date,
): CampaignSnapshotAudience {
	return {
		snapshotSet,
		snapshotAudienceSize: Math.max(0, Math.floor(audienceSize)),
		snapshotAt,
	};
}
